import type {
	AccessList,
	AdsCatalog,
	AdsCatalogInput,
	AdsTxt,
	MeResponse,
	Product,
	RedirectEntry,
	RedirectListPage,
	RedirectSearchResult,
	RefreshSummary,
	ServiceConfig,
	ServiceConfigInput,
	ServiceMeta,
	Shop,
	ShopInput,
	VideoConfig,
	VideoConfigInput,
} from './types';

export class ApiError extends Error {
	status: number;
	body: unknown;
	constructor(status: number, body: unknown, message: string) {
		super(message);
		this.status = status;
		this.body = body;
	}
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	const res = await fetch(path, {
		credentials: 'include',
		...init,
		headers: {
			'Content-Type': 'application/json',
			...(init?.headers ?? {}),
		},
	});
	const text = await res.text();
	const body = text ? safeJson(text) : null;
	if (!res.ok) {
		throw new ApiError(res.status, body, `${res.status} ${res.statusText}`);
	}
	return body as T;
}

function safeJson(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
}

export const api = {
	me: () => request<MeResponse>('/api/me'),
	services: () => request<ServiceMeta[]>('/api/services'),
	getAdsCatalog: () => request<AdsCatalog>('/api/ads-catalog'),
	putAdsCatalog: (body: AdsCatalogInput) =>
		request<AdsCatalog>('/api/ads-catalog', {
			method: 'PUT',
			body: JSON.stringify(body),
		}),
	getConfig: (service: string) => request<ServiceConfig>(`/api/config/${encodeURIComponent(service)}`),
	getWpExport: (service: string) =>
		request<Record<string, unknown>>(`/api/config/${encodeURIComponent(service)}/wp-export`),
	putConfig: (service: string, body: ServiceConfigInput) =>
		request<ServiceConfig>(`/api/config/${encodeURIComponent(service)}`, {
			method: 'PUT',
			body: JSON.stringify(body),
		}),
	redirects: {
		list: (service: string, opts?: { prefix?: string; cursor?: string; limit?: number }) => {
			const p = new URLSearchParams();
			if (opts?.prefix) p.set('prefix', opts.prefix);
			if (opts?.cursor) p.set('cursor', opts.cursor);
			if (opts?.limit) p.set('limit', String(opts.limit));
			const qs = p.toString();
			return request<RedirectListPage>(`/api/redirects/${encodeURIComponent(service)}${qs ? `?${qs}` : ''}`);
		},
		search: (service: string, q: string) =>
			request<RedirectSearchResult>(`/api/redirects/${encodeURIComponent(service)}/search?q=${encodeURIComponent(q)}`),
		get: (service: string, slug: string) =>
			request<RedirectEntry>(`/api/redirects/${encodeURIComponent(service)}/entry?slug=${encodeURIComponent(slug)}`),
		create: (service: string, slug: string, target: string) =>
			request<RedirectEntry>(`/api/redirects/${encodeURIComponent(service)}`, {
				method: 'POST',
				body: JSON.stringify({ slug, target }),
			}),
		update: (service: string, slug: string, target: string) =>
			request<RedirectEntry>(`/api/redirects/${encodeURIComponent(service)}`, {
				method: 'PUT',
				body: JSON.stringify({ slug, target }),
			}),
		remove: (service: string, slug: string) =>
			request<{ ok: true }>(`/api/redirects/${encodeURIComponent(service)}/entry?slug=${encodeURIComponent(slug)}`, { method: 'DELETE' }),
	},
	adstxt: {
		get: (service: string) => request<AdsTxt | null>(`/api/adstxt/${encodeURIComponent(service)}`),
		live: (service: string) =>
			request<{ content: string }>(`/api/adstxt/${encodeURIComponent(service)}/live`),
		put: (service: string, content: string) =>
			request<AdsTxt & { purgeFailed?: string[] }>(`/api/adstxt/${encodeURIComponent(service)}`, {
				method: 'PUT',
				body: JSON.stringify({ content }),
			}),
		// Zapis jest zablokowany bez zmiany treści, a brzeg potrafi zostać ze starym
		// plikiem po nieudanym purge'u — stąd osobne czyszczenie bez edycji.
		purge: (service: string) =>
			request<{ purgeFailed: string[] }>(`/api/adstxt/${encodeURIComponent(service)}/purge`, {
				method: 'POST',
			}),
	},
	video: {
		get: (service: string) => request<VideoConfig | null>(`/api/video/${encodeURIComponent(service)}`),
		put: (service: string, input: VideoConfigInput) =>
			request<VideoConfig>(`/api/video/${encodeURIComponent(service)}`, {
				method: 'PUT',
				body: JSON.stringify(input),
			}),
	},
	products: {
		list: (search = '') => request<Product[]>(`/api/products${search ? `?search=${encodeURIComponent(search)}` : ''}`),
		add: (id: string) =>
			request<{ ok: true; product: Product } | { ok: false; error: string }>('/api/products', {
				method: 'POST',
				body: JSON.stringify({ id }),
			}),
		remove: (id: string) => request<{ ok: true }>(`/api/products?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
		tags: () => request<Record<string, string>>('/api/products/tags'),
		setTag: (tag: string, category: string) =>
			request<{ ok: true }>('/api/products/tags', {
				method: 'POST',
				body: JSON.stringify({ tag, category }),
			}),
		refresh: () => request<RefreshSummary>('/api/products/refresh', { method: 'POST' }),
	},
	// Osobna lista moto (autoblog), ten sam kształt co products.
	motoProducts: {
		list: (search = '') =>
			request<Product[]>(`/api/products/moto${search ? `?search=${encodeURIComponent(search)}` : ''}`),
		add: (id: string) =>
			request<{ ok: true; product: Product } | { ok: false; error: string }>('/api/products/moto', {
				method: 'POST',
				body: JSON.stringify({ id }),
			}),
		remove: (id: string) =>
			request<{ ok: true }>(`/api/products/moto?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
	},
	shopsApi: {
		list: () => request<Shop[]>('/api/shops'),
		create: (body: ShopInput) => request<Shop>('/api/shops', { method: 'POST', body: JSON.stringify(body) }),
		update: (id: string, body: ShopInput) =>
			request<Shop>(`/api/shops/${encodeURIComponent(id)}`, {
				method: 'PUT',
				body: JSON.stringify(body),
			}),
		remove: (id: string) => request<{ ok: true }>(`/api/shops/${encodeURIComponent(id)}`, { method: 'DELETE' }),
		// Upload logo do Cloudflare Images przez worker (multipart, bez Content-Type
		// — przeglądarka ustawi boundary). Zwraca { id, url } z imagedelivery.net.
		uploadLogo: async (file: File, name: string): Promise<{ id: string; url: string }> => {
			const fd = new FormData();
			fd.append('file', file);
			fd.append('name', name);
			const res = await fetch('/api/shops/logo', {
				method: 'POST',
				credentials: 'include',
				body: fd,
			});
			const text = await res.text();
			const body = text ? safeJson(text) : null;
			if (!res.ok) throw new ApiError(res.status, body, `${res.status} ${res.statusText}`);
			return body as { id: string; url: string };
		},
	},
	getAccess: () => request<AccessList>('/api/access'),
	putAccess: (emails: string[]) =>
		request<AccessList>('/api/access', {
			method: 'PUT',
			body: JSON.stringify({ emails }),
		}),
	logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
};
